import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ALLOWED_DATA_SOURCE_SERVICES, type AllowedDataSourceService } from '@kfolks/types'
import { Button } from '@/shared/components/atoms/ui/button'
import { Input } from '@/shared/components/atoms/ui/input'
import { Modal } from '@/components/Modal'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/atoms/ui/table'
import { type DataSourceDTO, requestCenterApi } from '../api/request-center.api'
import { requestCenterKeys } from '../hooks/query-keys'
import { useCrudState } from '../hooks/useCrudState'

interface DataSourceForm {
	sourceKey: string
	name: string
	serviceName: AllowedDataSourceService
	endpoint: string
	labelField: string
	valueField: string
	cacheTtlSeconds: number
}

const blankForm = (): DataSourceForm => ({
	sourceKey: '',
	name: '',
	serviceName: 'employee-service',
	endpoint: '',
	labelField: 'name',
	valueField: 'id',
	cacheTtlSeconds: 300,
})

export function DataSourcesPage() {
	const crud = useCrudState<DataSourceDTO, DataSourceForm>()
	const { list, save } = useDataSourcesMutations(crud)
	return (
		<div className="space-y-4">
			<header className="flex items-center justify-between">
				<h1 className="text-2xl font-semibold tracking-tight">Form Data Sources</h1>
				<Button onClick={() => crud.openCreate(blankForm())}>New Data Source</Button>
			</header>
			<p className="text-sm text-muted-foreground">
				Live-data picklists for autocomplete fields. <code className="rounded bg-muted px-1">service_name</code> is
				constrained to four allow-listed services for SSRF defense.
			</p>
			<DataSourcesTable
				list={list}
				onEdit={(s) =>
					crud.openEdit(s, {
						sourceKey: s.sourceKey,
						name: s.name,
						serviceName: s.serviceName,
						endpoint: s.endpoint,
						labelField: s.labelField,
						valueField: s.valueField,
						cacheTtlSeconds: s.cacheTtlSeconds,
					})
				}
			/>
			<DataSourceFormModal
				open={crud.isOpen}
				editing={crud.isEditing}
				form={crud.form}
				saving={save.isPending}
				saveError={save.error}
				onChange={crud.setForm}
				onClose={crud.close}
				onSubmit={(input) => save.mutate(input)}
			/>
		</div>
	)
}

function useDataSourcesMutations(crud: ReturnType<typeof useCrudState<DataSourceDTO, DataSourceForm>>) {
	const queryClient = useQueryClient()
	const list = useQuery({
		queryKey: requestCenterKeys.dataSources.all,
		queryFn: () => requestCenterApi.listDataSources(),
	})
	const save = useMutation({
		mutationFn: async (input: DataSourceForm) =>
			crud.editing
				? requestCenterApi.updateDataSource(crud.editing.id, input as Partial<DataSourceDTO>)
				: requestCenterApi.createDataSource(input as Partial<DataSourceDTO>),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: requestCenterKeys.dataSources.all })
			crud.close()
		},
	})
	return { list, save }
}

function DataSourcesTable({
	list,
	onEdit,
}: {
	list: { data: DataSourceDTO[] | undefined; isLoading: boolean; error: unknown }
	onEdit: (s: DataSourceDTO) => void
}) {
	if (list.isLoading) return <TableSkeleton />
	if (list.error) return <ErrorState message="Failed to load data sources" />
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Key</TableHead>
					<TableHead>Service</TableHead>
					<TableHead>Endpoint</TableHead>
					<TableHead>TTL</TableHead>
					<TableHead className="w-24 text-right">Actions</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{(list.data ?? []).map((s) => (
					<TableRow key={s.id}>
						<TableCell className="font-mono text-xs">{s.sourceKey}</TableCell>
						<TableCell className="text-xs text-muted-foreground">{s.serviceName}</TableCell>
						<TableCell className="font-mono text-xs">{s.endpoint}</TableCell>
						<TableCell className="text-xs text-muted-foreground">{s.cacheTtlSeconds}s</TableCell>
						<TableCell className="text-right">
							<Button variant="outline" size="sm" onClick={() => onEdit(s)}>
								Edit
							</Button>
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

function DataSourceFormModal({
	open,
	editing,
	form,
	saving,
	saveError,
	onChange,
	onClose,
	onSubmit,
}: {
	open: boolean
	editing: boolean
	form: DataSourceForm | null
	saving: boolean
	saveError: unknown
	onChange: (next: DataSourceForm) => void
	onClose: () => void
	onSubmit: (input: DataSourceForm) => void
}) {
	if (!form) return null
	return (
		<Modal open={open} onClose={onClose} title={editing ? 'Edit data source' : 'New data source'} maxWidth="lg">
			<form
				onSubmit={(e) => {
					e.preventDefault()
					onSubmit(form)
				}}
				className="space-y-3"
			>
				<DataSourceFormFields form={form} onChange={onChange} />
				{saveError ? (
					<p role="alert" className="text-sm text-destructive">
						{messageFor(saveError)}
					</p>
				) : null}
				<FormActions saving={saving} onClose={onClose} />
			</form>
		</Modal>
	)
}

function DataSourceFormFields({ form, onChange }: { form: DataSourceForm; onChange: (next: DataSourceForm) => void }) {
	return (
		<>
			<TextRow label="Key" value={form.sourceKey} onChange={(v) => onChange({ ...form, sourceKey: v })} required />
			<TextRow label="Name" value={form.name} onChange={(v) => onChange({ ...form, name: v })} required />
			<label className="block space-y-1">
				<span className="text-sm font-medium">
					Service <span className="ml-0.5 text-destructive">*</span>
				</span>
				<select
					className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
					value={form.serviceName}
					onChange={(e) => onChange({ ...form, serviceName: e.target.value as AllowedDataSourceService })}
				>
					{ALLOWED_DATA_SOURCE_SERVICES.map((s) => (
						<option key={s} value={s}>
							{s}
						</option>
					))}
				</select>
			</label>
			<TextRow label="Endpoint" value={form.endpoint} onChange={(v) => onChange({ ...form, endpoint: v })} required />
			<div className="grid grid-cols-2 gap-3">
				<TextRow
					label="Label field"
					value={form.labelField}
					onChange={(v) => onChange({ ...form, labelField: v })}
					required
				/>
				<TextRow
					label="Value field"
					value={form.valueField}
					onChange={(v) => onChange({ ...form, valueField: v })}
					required
				/>
			</div>
			<label className="block space-y-1">
				<span className="text-sm font-medium">Cache TTL (seconds)</span>
				<Input
					type="number"
					value={form.cacheTtlSeconds}
					onChange={(e) => onChange({ ...form, cacheTtlSeconds: Number(e.target.value) || 0 })}
				/>
			</label>
		</>
	)
}

function FormActions({ saving, onClose }: { saving: boolean; onClose: () => void }) {
	return (
		<div className="flex justify-end gap-2 pt-2">
			<Button type="button" variant="outline" onClick={onClose}>
				Cancel
			</Button>
			<Button type="submit" disabled={saving}>
				{saving ? 'Saving…' : 'Save'}
			</Button>
		</div>
	)
}

function TextRow({
	label,
	value,
	onChange,
	required,
}: {
	label: string
	value: string
	onChange: (next: string) => void
	required?: boolean
}) {
	return (
		<label className="block space-y-1">
			<span className="text-sm font-medium">
				{label}
				{required ? <span className="ml-0.5 text-destructive">*</span> : null}
			</span>
			<Input value={value} onChange={(e) => onChange(e.target.value)} required={required} />
		</label>
	)
}

function TableSkeleton() {
	return (
		<div className="space-y-2">
			{[0, 1, 2].map((i) => (
				<div key={i} className="h-10 animate-pulse rounded bg-muted/40" />
			))}
		</div>
	)
}

function ErrorState({ message }: { message: string }) {
	return (
		<div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
			{message}
		</div>
	)
}

function messageFor(err: unknown): string {
	return err instanceof Error ? err.message : 'Save failed'
}
